import React, { createContext, useContext, useRef, useState, useCallback } from 'react';
import {
  Animated,
  Text,
  StyleSheet,
  View,
  Platform,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { haptics } from '../lib/haptics';
import theme from '../theme';

const { colors, spacing, radius, typography } = theme;

const ToastContext = createContext(null);

const ICONS = { success: '✓', error: '✕', warning: '⚠', info: 'ℹ' };
const BG = {
  success: { bg: 'rgba(52,211,153,0.15)', border: 'rgba(52,211,153,0.35)', color: '#34D399' },
  error:   { bg: 'rgba(248,113,113,0.15)', border: 'rgba(248,113,113,0.35)', color: '#F87171' },
  warning: { bg: 'rgba(251,191,36,0.15)', border: 'rgba(251,191,36,0.35)', color: '#FBBF24' },
  info:    { bg: 'rgba(79,142,247,0.15)', border: 'rgba(79,142,247,0.35)', color: '#4F8EF7' },
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const insets = useSafeAreaInsets();

  const show = useCallback(({ message, type = 'info', duration = 3000 }) => {
    const id = Date.now();
    const anim = new Animated.Value(0);

    if (type === 'success') haptics.success();
    else if (type === 'error') haptics.error();
    else if (type === 'warning') haptics.warning();
    else haptics.light();

    setToasts((prev) => [...prev, { id, message, type, anim }]);

    Animated.sequence([
      Animated.spring(anim, { toValue: 1, useNativeDriver: true, tension: 80, friction: 10 }),
      Animated.delay(duration),
      Animated.timing(anim, { toValue: 0, duration: 250, useNativeDriver: true }),
    ]).start(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    });
  }, []);

  const toast = {
    show,
    success: (message, opts) => show({ message, type: 'success', ...opts }),
    error:   (message, opts) => show({ message, type: 'error', ...opts }),
    warning: (message, opts) => show({ message, type: 'warning', ...opts }),
    info:    (message, opts) => show({ message, type: 'info', ...opts }),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <View style={[styles.container, { top: insets.top + 12 }]} pointerEvents="none">
        {toasts.map(({ id, message, type, anim }) => {
          const style = BG[type] || BG.info;
          return (
            <Animated.View
              key={id}
              style={[
                styles.toast,
                { backgroundColor: style.bg, borderColor: style.border },
                {
                  opacity: anim,
                  transform: [{
                    translateY: anim.interpolate({ inputRange: [0, 1], outputRange: [-20, 0] }),
                  }],
                },
              ]}
            >
              <View style={[styles.iconCircle, { backgroundColor: style.border }]}>
                <Text style={[styles.icon, { color: style.color }]}>{ICONS[type]}</Text>
              </View>
              <Text style={styles.message} numberOfLines={2}>{message}</Text>
            </Animated.View>
          );
        })}
      </View>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    left: spacing.xl,
    right: spacing.xl,
    zIndex: 9999,
    gap: spacing.sm,
  },
  toast: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: radius.lg,
    borderWidth: 1,
    padding: spacing.md,
    gap: spacing.md,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.3,
        shadowRadius: 16,
      },
      android: { elevation: 12 },
    }),
  },
  iconCircle: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  icon: {
    fontSize: 12,
    fontWeight: '700',
  },
  message: {
    flex: 1,
    fontSize: typography.sizes.sm,
    fontWeight: '500',
    color: colors.text,
    lineHeight: typography.sizes.sm * 1.5,
  },
});
